(() => {
  const TAG = 'site-header';

  if (customElements.get(TAG)) return;

  const megaToggleFrom = (target) => {
    if (!(target instanceof Element)) return null;

    const toggle = target.closest('[data-mega-toggle]');
    if (toggle) return toggle;

    return target.closest('.header__nav-parent')?.querySelector('[data-mega-toggle]') ?? null;
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  // A tap still has to move focus into the dialog. focusVisible: false keeps the
  // ring off for that pointer move. Older browsers ignore the option and match
  // :focus-visible anyway, so the attribute hides the ring until the next blur.
  // preventScroll is only for a target already on screen. The close control is
  // focused while the drawer is still sliding in, and the menu button sits in
  // the sticky header. Tab and Back must be allowed to scroll, or the focused
  // control stays below the fold.
  const moveFocus = (element, focusVisible = true, preventScroll = false) => {
    if (!(element instanceof HTMLElement)) return;

    if (focusVisible) {
      element.removeAttribute('data-suppress-focus');
    } else if (!element.hasAttribute('data-suppress-focus')) {
      element.setAttribute('data-suppress-focus', '');
      element.addEventListener(
        'blur',
        () => {
          element.removeAttribute('data-suppress-focus');
        },
        { once: true }
      );
    }

    element.focus({ preventScroll, focusVisible });
  };

  class SiteHeader extends HTMLElement {
    #mobileOpen = false;
    #mobileClosing = false;
    #activePanel = null;
    #opener = null;
    #closeTimer = null;
    #hoverOpenTimer = null;
    #pendingHoverTrigger = null;
    #restoreFocusAfterClose = false;
    #restoreFocusVisible = true;
    #focusOutTimer = null;
    #pointerInsideMenu = false;
    #overlayAllowed = null;
    #overlayFrame = null;

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      this.addEventListener('pointerdown', this.#onPointerDown);
      this.addEventListener('pointerover', this.#onPointerOver);
      this.addEventListener('pointerleave', this.#onPointerLeave);
      this.addEventListener('focusin', this.#onFocusIn);
      this.addEventListener('focusout', this.#onFocusOut);
      document.addEventListener('click', this.#onDocumentClick);
      document.addEventListener('keydown', this.#onKeydown);
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      window.addEventListener('resize', this.#onResize);
      // Editing, reordering, or removing the first section changes what the
      // header is overlaying without reloading the header itself.
      document.addEventListener('shopify:section:load', this.#onSectionChange);
      document.addEventListener('shopify:section:unload', this.#onSectionChange);
      document.addEventListener('shopify:section:reorder', this.#onSectionChange);

      // Latched on the first connection: the sync below turns the attribute off
      // when the overlay does not hold, and re-reading it would make that
      // permanent.
      if (this.#overlayAllowed === null) {
        this.#overlayAllowed = this.getAttribute('data-overlay') === 'true';
      }

      // Tone first and synchronously. It needs no measurement, and deferring it
      // to the frame below would leave a dark hero rendering the primary text
      // tone until then.
      this.#syncTone();
      this.#scheduleOverlaySync();
      // A late webfont can change the rendered header height, which is what the
      // overlay is measured against.
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.#scheduleOverlaySync();
      });
      this.#syncScrollState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('pointerdown', this.#onPointerDown);
      this.removeEventListener('pointerover', this.#onPointerOver);
      this.removeEventListener('pointerleave', this.#onPointerLeave);
      this.removeEventListener('focusin', this.#onFocusIn);
      this.removeEventListener('focusout', this.#onFocusOut);
      document.removeEventListener('click', this.#onDocumentClick);
      document.removeEventListener('keydown', this.#onKeydown);
      window.removeEventListener('scroll', this.#onScroll);
      window.removeEventListener('resize', this.#onResize);
      document.removeEventListener('shopify:section:load', this.#onSectionChange);
      document.removeEventListener('shopify:section:unload', this.#onSectionChange);
      document.removeEventListener('shopify:section:reorder', this.#onSectionChange);
      this.#cancelHoverOpen();
      this.#cancelFocusOut();
      this.#cancelOverlaySync();
      document.documentElement.style.overflow = '';
      const menu = this.querySelector('[data-mobile-menu]');
      if (menu) this.#cancelPendingHide(menu);
    }

    #onClick = (event) => {
      const mobileToggle = event.target.closest('[data-mobile-menu-toggle]');
      const next = event.target.closest('[data-mobile-next]');
      const back = event.target.closest('[data-mobile-back]');
      // Keyboard activation of a button clicks with detail 0. A tap or mouse
      // click does not, and should not paint a focus ring on the control focus
      // then moves to.
      const focusVisible = event.detail === 0;

      if (event.target.closest('[data-quick-cart-open]') && this.#mobileOpen) {
        this.#closeMobile(false, false);
      }

      if (mobileToggle) {
        this.#mobileOpen
          ? this.#closeMobile(true, true, focusVisible)
          : this.#openMobile(mobileToggle, focusVisible);
        return;
      }

      if (event.target.closest('[data-mobile-menu-close]')) {
        this.#closeMobile(true, true, focusVisible);
        return;
      }

      if (next) {
        this.#showMobilePage(next.getAttribute('data-mobile-next'), next, focusVisible);
        return;
      }

      if (back) {
        this.#showMobileRoot(back, focusVisible);
        return;
      }

      const megaToggle = event.target.closest('[data-mega-toggle]');
      if (!megaToggle || !window.matchMedia('(min-width: 990px)').matches) return;

      if (megaToggle.getAttribute('aria-expanded') === 'true') this.#closePanel();
      else this.#openPanel(megaToggle);
    };

    #onPointerOver = (event) => {
      if (!window.matchMedia('(min-width: 990px)').matches) return;

      const trigger = megaToggleFrom(event.target);
      if (trigger) {
        this.#scheduleOpenPanel(trigger);
        return;
      }

      if (event.target.closest('[data-mega-panel]')) {
        this.#cancelHoverOpen();
        return;
      }

      /* The row's gaps are neither a trigger nor a panel. Closing on them would
         drop an open panel mid-sweep and send the next item back through the
         delay, so they only call off a pending open. */
      if (event.target.closest('[data-mega-row]') && !event.target.closest('a')) {
        this.#cancelHoverOpen();
        return;
      }

      this.#cancelHoverOpen();
      this.#closePanel();
    };

    #onPointerLeave = () => {
      if (!window.matchMedia('(min-width: 990px)').matches) return;

      this.#cancelHoverOpen();
      if (this.contains(document.activeElement)) return;

      this.#closePanel();
    };

    // Focusing a parent link follows that link. The panel opens from the button
    // beside it, or from hover. Tabbing onto either must not open a panel.
    #onFocusIn = (event) => {
      if (!window.matchMedia('(min-width: 990px)').matches) return;

      if (event.target.closest('predictive-search')) {
        this.#closePanel();
        return;
      }

      if (this.#activePanel && this.#megaFocusWithin(this.#activePanel, event.target)) return;

      this.#closePanel();
    };

    // focusin only hears a target inside the header. Tabbing out of it — into
    // the page, or back to the skip link — never arrives there, and pointerleave
    // also holds an open panel while focus is still anywhere in the header. By
    // the time that focus then leaves, nothing else closes the panel.
    // relatedTarget is the element about to be focused, so this can close before
    // focusin. A null target means focus left the document; that has to wait
    // until focus has settled, or a move still inside the panel is read as body.
    // A click on panel padding or a non-link card also blurs to the body. Only
    // that pointer press may keep the panel open. A keyboard exit still closes
    // it while the pointer is resting on the menu.
    #onPointerDown = () => {
      this.#pointerInsideMenu = true;

      // A click that does not move focus must not count as the next keyboard exit.
      window.setTimeout(() => {
        this.#pointerInsideMenu = false;
      }, 0);
    };

    #onFocusOut = (event) => {
      if (!window.matchMedia('(min-width: 990px)').matches) return;

      const panel = this.#activePanel;
      if (!panel) return;

      this.#cancelFocusOut();

      const fromPointer = this.#pointerInsideMenu;
      this.#pointerInsideMenu = false;

      const next = event.relatedTarget;
      if (next instanceof Element) {
        if (!this.#megaFocusWithin(panel, next)) this.#closePanel();
        return;
      }

      this.#focusOutTimer = window.setTimeout(() => {
        this.#focusOutTimer = null;
        if (this.#activePanel !== panel) return;
        if (this.#megaFocusWithin(panel, document.activeElement)) return;
        if (fromPointer && (this.matches(':hover') || panel.matches(':hover'))) return;

        this.#closePanel();
      }, 0);
    };

    #cancelFocusOut() {
      if (this.#focusOutTimer === null) return;

      window.clearTimeout(this.#focusOutTimer);
      this.#focusOutTimer = null;
    }

    #megaFocusWithin(panel, active) {
      if (!(active instanceof Element)) return false;
      if (panel.contains(active)) return true;

      const trigger = this.querySelector(`[aria-controls="${panel.id}"]`);
      return trigger === active || !!trigger?.contains(active);
    }

    #onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.#closePanel();
    };

    #onKeydown = (event) => {
      if (document.querySelector('quick-cart[data-open]')) return;

      if (event.key === 'Escape') {
        if (this.#mobileOpen) {
          this.#closeMobile();
          return;
        }

        if (this.#activePanel) {
          const trigger = this.querySelector(`[aria-controls="${this.#activePanel.id}"]`);
          this.#closePanel();
          moveFocus(trigger, true);
        }
      }

      if (event.key === 'Tab' && this.#mobileOpen) {
        if (event.target instanceof Element && event.target.closest('predictive-search[data-open]')) {
          return;
        }
        this.#trapMobileFocus(event);
      }
    };

    #onScroll = () => this.#syncScrollState();

    #onResize = () => {
      if (window.matchMedia('(min-width: 990px)').matches && this.#mobileOpen) {
        this.#closeMobile(false, false);
      }

      this.#scheduleOverlaySync();
    };

    #onSectionChange = () => this.#scheduleOverlaySync();

    #openPanel(trigger) {
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (!panel) return;

      document.dispatchEvent(new CustomEvent('predictive_search_close'));
      this.#closePanel();
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.#activePanel = panel;
      this.setAttribute('data-menu-open', 'true');
    }

    #scheduleOpenPanel(trigger) {
      const panelId = trigger.getAttribute('aria-controls');
      if (this.#activePanel && this.#activePanel.id === panelId) {
        this.#cancelHoverOpen();
        return;
      }

      if (this.#activePanel) {
        this.#openPanel(trigger);
        return;
      }

      if (this.#pendingHoverTrigger === trigger) return;

      this.#cancelHoverOpen();
      this.#pendingHoverTrigger = trigger;
      this.#hoverOpenTimer = window.setTimeout(() => {
        this.#hoverOpenTimer = null;
        this.#pendingHoverTrigger = null;
        this.#openPanel(trigger);
      }, 400);
    }

    #cancelHoverOpen() {
      this.#pendingHoverTrigger = null;

      if (this.#hoverOpenTimer === null) return;

      window.clearTimeout(this.#hoverOpenTimer);
      this.#hoverOpenTimer = null;
    }

    #closePanel() {
      this.#cancelHoverOpen();
      if (!this.#activePanel) return;

      const trigger = this.querySelector(`[aria-controls="${this.#activePanel.id}"]`);
      trigger?.setAttribute('aria-expanded', 'false');
      this.#activePanel.hidden = true;
      this.#activePanel = null;
      this.setAttribute('data-menu-open', 'false');
    }

    #openMobile(opener, focusVisible = true) {
      const menu = this.querySelector('[data-mobile-menu]');
      if (!menu) return;

      this.#opener = opener;
      this.#cancelPendingHide(menu);
      this.#mobileClosing = false;
      this.#restoreFocusAfterClose = false;
      this.#showMobileRoot();
      this.#mobileOpen = true;
      this.setAttribute('data-mobile-open', 'true');
      this.setAttribute('data-menu-open', 'true');
      menu.hidden = false;
      // Committing layout here lets the off-canvas start position take effect
      // before the open attribute lands, so revealing and opening in one task
      // still animates.
      menu.getBoundingClientRect();
      menu.setAttribute('data-open', '');
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      this.#syncToggleIcon(true);
      moveFocus(this.#mobileFocusables()[0], focusVisible, true);
    }

    #closeMobile(restoreFocus = true, animate = true, focusVisible = true) {
      const menu = this.querySelector('[data-mobile-menu]');
      const toggle = this.querySelector('[data-mobile-menu-toggle]');

      if (!menu) return;

      // The drawer holds its own predictive search, which would still be open
      // behind the hidden drawer and reappear the next time it is opened.
      // reset clears that field only, so the header search keeps a typed or
      // results-page query.
      document.dispatchEvent(
        new CustomEvent('predictive_search_close', { detail: { reset: true, resetRoot: menu } })
      );

      this.#mobileClosing = true;
      this.#restoreFocusAfterClose = restoreFocus;
      this.#restoreFocusVisible = focusVisible;
      menu.removeAttribute('data-open');
      toggle?.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      this.#syncToggleIcon(false);
      this.#hideMobile(menu, animate);
    }

    #hideMobile(menu, animate) {
      // No transition runs when motion is reduced, nor at desktop widths where
      // the drawer is display:none, so waiting for one would leave it revealed.
      const skipTransition =
        !animate ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        window.matchMedia('(min-width: 990px)').matches;

      if (skipTransition) {
        this.#finishHideMobile(menu);
        return;
      }

      menu.addEventListener('transitionend', this.#onMenuTransitionEnd);
      this.#closeTimer = window.setTimeout(() => this.#finishHideMobile(menu), 300);
    }

    #onMenuTransitionEnd = (event) => {
      if (
        event.target !== event.currentTarget ||
        event.propertyName !== 'transform' ||
        !this.#mobileClosing
      ) {
        return;
      }

      this.#finishHideMobile(event.currentTarget);
    };

    #finishHideMobile(menu) {
      if (!this.#mobileClosing) return;

      this.#cancelPendingHide(menu);
      menu.hidden = true;
      this.removeAttribute('data-mobile-open');
      this.setAttribute('data-menu-open', 'false');
      this.#showMobileRoot();
      this.#mobileOpen = false;
      this.#mobileClosing = false;

      if (this.#restoreFocusAfterClose) moveFocus(this.#opener, this.#restoreFocusVisible, true);
      this.#restoreFocusAfterClose = false;
    }

    #cancelPendingHide(menu) {
      menu.removeEventListener('transitionend', this.#onMenuTransitionEnd);

      if (this.#closeTimer !== null) {
        window.clearTimeout(this.#closeTimer);
        this.#closeTimer = null;
      }
    }

    #showMobilePage(handle, opener, focusVisible = true) {
      const root = this.querySelector('[data-mobile-root]');
      const page = this.querySelector(`[data-mobile-page="${CSS.escape(handle)}"]`);
      if (!root || !page) return;

      root.hidden = true;
      for (const item of this.querySelectorAll('[data-mobile-page]')) item.hidden = true;
      page.hidden = false;
      page.dataset.mobileOpener = opener?.getAttribute('data-mobile-next') || '';
      moveFocus(page.querySelector('[data-mobile-back]'), focusVisible);
    }

    #showMobileRoot(backButton, focusVisible = true) {
      const root = this.querySelector('[data-mobile-root]');
      if (!root) return;

      const currentPage = backButton?.closest('[data-mobile-page]');
      const openerHandle = currentPage?.dataset.mobileOpener;
      for (const item of this.querySelectorAll('[data-mobile-page]')) item.hidden = true;
      root.hidden = false;

      if (openerHandle) {
        moveFocus(
          root.querySelector(`[data-mobile-next="${CSS.escape(openerHandle)}"]`),
          focusVisible
        );
      }
    }

    // The header toggle sits behind the open drawer, which carries its own
    // close button, so the trap stays inside the panel.
    #mobileFocusables() {
      const menu = this.querySelector('[data-mobile-menu]');
      const candidates = menu ? [...menu.querySelectorAll(focusableSelector)] : [];

      return candidates.filter((item) => this.#isMobileTrapTarget(item));
    }

    #isMobileTrapTarget(item) {
      if (item.closest('[hidden]')) return false;

      // Some controls are hidden by stylesheet rather than the attribute above,
      // notably the locale picker, which ships a disclosure and a native select
      // and hides whichever one the viewport doesn't use. Focusing those is a
      // no-op, which would stall the cycle.
      if (item.getClientRects().length === 0) return false;

      const closedDetails = item.closest('details:not([open])');
      if (!closedDetails) return true;

      const summary = closedDetails.querySelector(':scope > summary');
      return item === summary || Boolean(summary?.contains(item));
    }

    #trapMobileFocus(event) {
      const focusable = this.#mobileFocusables();
      if (focusable.length === 0) return;

      event.preventDefault();

      const index = focusable.indexOf(document.activeElement);

      if (index === -1) {
        moveFocus(event.shiftKey ? focusable[focusable.length - 1] : focusable[0], true);
        return;
      }

      const nextIndex = event.shiftKey
        ? (index - 1 + focusable.length) % focusable.length
        : (index + 1) % focusable.length;

      moveFocus(focusable[nextIndex], true);
    }

    #syncToggleIcon(open) {
      const menuIcon = this.querySelector('[data-menu-icon]');
      const closeIcon = this.querySelector('[data-close-icon]');
      if (menuIcon) menuIcon.hidden = open;
      if (closeIcon) closeIcon.hidden = !open;
    }

    #syncScrollState() {
      this.setAttribute('data-at-top', window.scrollY <= 8 ? 'true' : 'false');
    }

    // Measuring forces layout, so it is held to a frame. Resize fires in bursts
    // and the connected sync has to wait for styles to apply regardless.
    #scheduleOverlaySync() {
      if (!this.#overlayAllowed || this.#overlayFrame !== null) return;

      this.#overlayFrame = window.requestAnimationFrame(() => {
        this.#overlayFrame = null;
        this.#syncOverlay();
      });
    }

    #cancelOverlaySync() {
      if (this.#overlayFrame === null) return;

      window.cancelAnimationFrame(this.#overlayFrame);
      this.#overlayFrame = null;
    }

    /* The transparent header has no surface of its own, so its tone is only
       legible while the hero is pulled up behind it. Where that does not hold —
       an element injected between the header and the main content, a first
       section shorter than the pull-up, a rendered header taller than
       --header-height — the header would paint its hero tone over the page
       background instead. Confirming it is overlaid before allowing the state
       keeps the failure at a solid header rather than an invisible one. */
    #syncOverlay() {
      // Restored before measuring so the geometry is read with the overlay
      // layout applied and a withdrawn state can recover.
      this.setAttribute('data-overlay', 'true');
      this.#syncTone();

      if (!this.#isOverlaid(this.#overlaidSection())) this.setAttribute('data-overlay', 'false');
    }

    // Reads no geometry, so it can run before the first paint.
    #syncTone() {
      const tone = this.#overlaidSection()?.getAttribute('data-header-tone');

      if (tone === 'light' || tone === 'dark') this.setAttribute('data-tone', tone);
      else this.removeAttribute('data-tone');
    }

    // Only the first section is pulled under the nav, so it is the only one
    // whose tone the header can read.
    #overlaidSection() {
      const first = document.querySelector('main')?.firstElementChild;

      if (!first) return null;

      return first.matches('[data-header-tone]') ? first : first.querySelector('[data-header-tone]');
    }

    #isOverlaid(hero) {
      const section = this.closest('.header-section');

      if (!hero || !section) return false;

      const heroBox = hero.getBoundingClientRect();
      const headerBox = section.getBoundingClientRect();

      /* Tolerances are sub-pixel drift and a hairline border only. A larger gap
         means the section starts below the header rather than behind it, and a
         section shorter than the header leaves the bottom rows on whatever
         follows. Height rather than the bottom edge, so the answer does not
         depend on where the page happens to be scrolled. */
      return heroBox.top - headerBox.top <= 2 && heroBox.height >= headerBox.height - 2;
    }
  }

  customElements.define(TAG, SiteHeader);
})();
